import React, { useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography
} from '@mui/material';
import MaintenanceBarcodeLabel from './MaintenanceBarcodeLabel';
import { MaintenanceRecord } from '../utils/types';

interface PrintBarcodeDialogProps {
  open: boolean;
  onClose: () => void;
  maintenance: MaintenanceRecord;
  selectedDeviceIndex?: number; // لطباعة جهاز محدد في حالة الأجهزة المتعددة
}

const PrintBarcodeDialog: React.FC<PrintBarcodeDialogProps> = ({ open, onClose, maintenance, selectedDeviceIndex }) => {
  const printRef = useRef<HTMLDivElement>(null);
  const [labelSize, setLabelSize] = React.useState<string>('tiny');
  const [printAllDevices, setPrintAllDevices] = React.useState<boolean>(false);
  const [currentSelectedDeviceIndex, setCurrentSelectedDeviceIndex] = React.useState<number>(selectedDeviceIndex || 0);
  const [isPrinting, setIsPrinting] = React.useState<boolean>(false);

  // Cargar el tamaño guardado del localStorage si está disponible
  React.useEffect(() => {
    const savedSize = localStorage.getItem('labelSize');
    if (savedSize) {
      setLabelSize(savedSize);
    }
  }, []);

  // إنشاء سجل صيانة لجهاز محدد
  const createMaintenanceForDevice = (deviceIndex: number) => {
    if (!maintenance.devices || maintenance.devices.length === 0) {
      return maintenance; // إرجاع السجل الأصلي إذا لم تكن هناك أجهزة متعددة
    }

    const device = maintenance.devices[deviceIndex];
    return {
      ...maintenance,
      deviceType: device.deviceType,
      issue: device.issue,
      accessories: device.accessories || [],
      usedParts: device.usedParts || []
    };
  };

  // Función de impresión que usa la API nativa del navegador
  const handlePrint = () => {
    if (isPrinting) return; // منع الطباعة المتعددة

    setIsPrinting(true);

    // Guardar el tamaño seleccionado en localStorage
    localStorage.setItem('labelSize', labelSize);

    // تحديد الأجهزة المراد طباعتها
    const devicesToPrint = [];
    if (printAllDevices && maintenance.devices && maintenance.devices.length > 1) {
      // طباعة جميع الأجهزة
      console.log('Printing all devices:', maintenance.devices.length);
      for (let i = 0; i < maintenance.devices.length; i++) {
        devicesToPrint.push(createMaintenanceForDevice(i));
      }
    } else {
      // طباعة جهاز واحد
      const deviceIndex = currentSelectedDeviceIndex || 0;
      console.log('Printing single device at index:', deviceIndex);
      devicesToPrint.push(createMaintenanceForDevice(deviceIndex));
    }

    console.log('Devices to print:', devicesToPrint.length);

    // طباعة كل جهاز
    printDevicesSequentially(devicesToPrint, 0);
  };

  // طباعة الأجهزة بشكل متتالي
  const printDevicesSequentially = async (devicesToPrint: any[], currentIndex: number) => {
    if (currentIndex >= devicesToPrint.length) {
      // انتهت الطباعة
      setIsPrinting(false);
      onClose();
      return;
    }

    const deviceMaintenance = devicesToPrint[currentIndex];

    console.log(`Printing device ${currentIndex + 1} of ${devicesToPrint.length}:`, deviceMaintenance.deviceType);

    await printSingleDevice(deviceMaintenance, () => {
      // الانتقال للجهاز التالي بعد تأخير قصير
      setTimeout(() => {
        printDevicesSequentially(devicesToPrint, currentIndex + 1);
      }, 1000);
    });
  };

  const printSingleDevice = async (deviceMaintenance: any, onComplete?: () => void) => {
    // الحصول على رقم هاتف العميل
    let customerPhone = 'غير متوفر';
    if (deviceMaintenance.customerId) {
      try {
        const { getCustomerById } = await import('../services/customerService');
        const customer = await getCustomerById(deviceMaintenance.customerId);
        if (customer && customer.phone) {
          customerPhone = customer.phone;
        }
      } catch (error) {
        console.error('Error loading customer phone for print:', error);
      }
    }

    // الحصول على إعدادات النظام
    let companyName = 'مركز صيانة التقنية';
    let companyPhone = '';
    try {
      const { getSystemSettings } = await import('../services/systemSettingsService');
      const settings = await getSystemSettings();
      console.log('🏢 Print - System settings loaded:', settings);
      if (settings && settings.companyName) {
        companyName = settings.companyName;
        console.log('🏢 Print - Company name set to:', companyName);
      } else {
        console.log('🏢 Print - No company name found in settings, using default');
      }
      if (settings && settings.companyPhone) {
        companyPhone = settings.companyPhone;
        console.log('🏢 Print - Company phone set to:', companyPhone);
      }
    } catch (error) {
      console.error('Error loading company settings for print:', error);
    }

    // Crear un iframe oculto para la impresión
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'absolute';
    printFrame.style.top = '-9999px';
    printFrame.style.left = '-9999px';
    document.body.appendChild(printFrame);

    // Obtener el contenido del documento
    const printDocument = printFrame.contentDocument;
    if (printDocument) {
      // إنشاء مكون الملصق للجهاز المحدد
      console.log('🖨️ Creating print HTML with company name:', companyName);
      console.log('🖨️ Customer phone:', customerPhone);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div style="
          width: ${labelSize === 'tiny' ? '46mm' : '54mm'};
          height: ${labelSize === 'tiny' ? '21mm' : labelSize === 'small' ? '36mm' : labelSize === 'medium' ? '56mm' : '76mm'};
          padding: ${labelSize === 'tiny' ? '1.5mm' : '2mm'};
          box-sizing: border-box;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          justify-content: ${labelSize === 'tiny' ? 'space-between' : 'center'};
          align-items: center;
          text-align: center;
          font-family: 'Cairo', sans-serif;
        ">
          <div style="
            flex: ${labelSize === 'tiny' ? '0 0 auto' : 'none'};
            width: 100%;
            text-align: center;
          ">
            <h1 style="
              margin: 0;
              font-size: ${labelSize === 'tiny' ? '12px' : labelSize === 'small' ? '16px' : '18px'};
              text-align: center;
              width: 100%;
              font-weight: bold;
              line-height: ${labelSize === 'tiny' ? '1.1' : '1.2'};
            ">${companyName}</h1>
            <p style="
              margin: 0;
              font-size: ${labelSize === 'tiny' ? '9px' : labelSize === 'small' ? '12px' : '14px'};
              text-align: center;
              width: 100%;
              line-height: ${labelSize === 'tiny' ? '1.1' : '1.2'};
            ">${companyPhone}</p>
          </div>
          <div style="
            display: flex;
            flex-direction: column;
            gap: ${labelSize === 'tiny' ? '0.5mm' : '1mm'};
            flex: ${labelSize === 'tiny' ? '1 1 auto' : 'none'};
            justify-content: ${labelSize === 'tiny' ? 'space-evenly' : 'flex-start'};
            width: 100%;
          ">
            <p style="
              margin: 0;
              font-size: ${labelSize === 'tiny' ? '11px' : labelSize === 'small' ? '15px' : '17px'};
              font-weight: bold;
              width: 100%;
              line-height: ${labelSize === 'tiny' ? '1.1' : '1.2'};
            ">العميل: ${deviceMaintenance.customerName}</p>
            <div style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              width: 100%;
              margin: 0;
            ">
              <p style="
                margin: 0;
                font-size: ${labelSize === 'tiny' ? '10px' : labelSize === 'small' ? '14px' : '16px'};
                line-height: ${labelSize === 'tiny' ? '1.1' : '1.2'};
                flex: 1;
              ">${customerPhone}</p>
              <p style="
                margin: 0;
                font-size: ${labelSize === 'tiny' ? '12px' : labelSize === 'small' ? '16px' : '18px'};
                line-height: ${labelSize === 'tiny' ? '1.1' : '1.2'};
                font-weight: bold;
                margin-left: 2mm;
              ">
                ${deviceMaintenance.orderNumber || 1}${deviceMaintenance.devices && deviceMaintenance.devices[0]?.size ? ` - ${deviceMaintenance.devices[0].size}\"` : ''}
              </p>
            </div>
            <p style="
              margin: 0;
              font-size: ${labelSize === 'tiny' ? '10px' : labelSize === 'small' ? '14px' : '16px'};
              width: 100%;
              font-weight: bold;
              line-height: ${labelSize === 'tiny' ? '1.1' : '1.2'};
            ">المشكلة: ${deviceMaintenance.issue?.substring(0, labelSize === 'tiny' ? 12 : 30)}${deviceMaintenance.issue?.length > (labelSize === 'tiny' ? 12 : 30) ? '...' : ''}</p>
            ${deviceMaintenance.devices && deviceMaintenance.devices[0]?.brand ? `<p style="margin:0;font-size:${labelSize === 'tiny' ? '10px' : labelSize === 'small' ? '14px' : '16px'};width:100%;line-height:1.2;">${deviceMaintenance.devices[0].brand}</p>` : ''}
          </div>
        </div>
      `;

      // Crear el contenido HTML para la impresión
      printDocument.open();
      printDocument.write(`
        <html>
          <head>
            <title>ملصق الصيانة - ${deviceMaintenance.deviceType}</title>
            <style>
              @page {
                /* Tamaño exacto del ملصق */
                size: ${labelSize === 'tiny' ? '50mm' : '58mm'} ${labelSize === 'tiny' ? '25mm' : labelSize === 'small' ? '40mm' : labelSize === 'medium' ? '60mm' : '80mm'};
                margin: 0;
              }
              @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap');

              html, body {
                margin: 0;
                padding: 0;
                width: ${labelSize === 'tiny' ? '50mm' : '58mm'};
                height: ${labelSize === 'tiny' ? '25mm' : labelSize === 'small' ? '40mm' : labelSize === 'medium' ? '60mm' : '80mm'};
                overflow: hidden;
                font-family: 'Cairo', sans-serif;
              }
            </style>
          </head>
          <body>
            ${tempDiv.innerHTML}
          </body>
        </html>
      `);
      printDocument.close();

      // Esperar a que se carguen los estilos y las imágenes
      setTimeout(() => {
        try {
          if (printFrame.contentWindow) {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
          }

          // Eliminar el iframe después de imprimir
          setTimeout(() => {
            document.body.removeChild(printFrame);
            if (onComplete) {
              onComplete();
            }
          }, 500);
        } catch (error) {
          console.error('Error printing:', error);
          document.body.removeChild(printFrame);
          alert('حدث خطأ أثناء الطباعة. يرجى المحاولة مرة أخرى.');
          if (onComplete) {
            onComplete();
          }
        }
      }, 500);
    } else {
      document.body.removeChild(printFrame);
      alert('حدث خطأ أثناء إعداد الطباعة. يرجى المحاولة مرة أخرى.');
      if (onComplete) {
        onComplete();
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>طباعة ملصق</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <FormControl fullWidth margin="normal">
            <InputLabel id="label-size-select-label">حجم الملصق</InputLabel>
            <Select
              labelId="label-size-select-label"
              value={labelSize}
              label="حجم الملصق"
              onChange={(e) => setLabelSize(e.target.value)}
            >
              <MenuItem value="tiny">50مم x 25مم (متناهي الصغر)</MenuItem>
              <MenuItem value="small">58مم x 40مم (صغير)</MenuItem>
              <MenuItem value="medium">58مم x 60مم (متوسط)</MenuItem>
              <MenuItem value="large">58مم x 80مم (كبير)</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* خيارات الطباعة للأجهزة المتعددة */}
        {maintenance.devices && maintenance.devices.length > 1 && (
          <Box mb={2}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="device-select-label">اختر الجهاز للطباعة</InputLabel>
              <Select
                labelId="device-select-label"
                value={currentSelectedDeviceIndex}
                label="اختر الجهاز للطباعة"
                onChange={(e) => {
                  setCurrentSelectedDeviceIndex(Number(e.target.value));
                }}
              >
                {maintenance.devices.map((device, index) => (
                  <MenuItem key={index} value={index}>
                    {device.deviceType} - {device.brand} ({device.issue.substring(0, 20)}...)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={printAllDevices}
                    onChange={(e) => setPrintAllDevices(e.target.checked)}
                  />
                }
                label={`طباعة ملصقات لجميع الأجهزة (${maintenance.devices?.length || 0} أجهزة)`}
              />
              {printAllDevices && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="caption" color="info.dark">
                    سيتم طباعة ملصق منفصل لكل جهاز من الأجهزة الـ {maintenance.devices?.length || 0}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <div ref={printRef}>
            <MaintenanceBarcodeLabel
              maintenance={
                maintenance.devices && maintenance.devices.length > 1
                  ? createMaintenanceForDevice(currentSelectedDeviceIndex)
                  : maintenance
              }
              labelSize={labelSize}
            />
          </div>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>إلغاء</Button>
        <Button
          onClick={handlePrint}
          variant="contained"
          color="primary"
          disabled={isPrinting}
        >
          {isPrinting ? 'جاري الطباعة...' : printAllDevices && maintenance.devices && maintenance.devices.length > 1 ? `طباعة ${maintenance.devices.length} ملصقات` : 'طباعة'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintBarcodeDialog;
